import React, { useState } from "react";
import {
  FaCircleCheck,
  FaMagnifyingGlass,
  FaSquareCaretDown, // Bulk-CV icon
  FaFileExcel, // Excel icon
} from "react-icons/fa6";
import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../../config/api";

const asObj = (val) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  return { candidateId: String(val) };
};

const friendlyNotify = (title, message, color = "blue") => {
  notifications.show({
    title,
    message,
    color,
    autoClose: 2500,
    withCloseButton: true,
    radius: "md",
    styles: {
      root: {
        padding: "10px 12px",
        border: "1px solid #E5E7EB",
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        backgroundColor: "#ffffff",
      },
      title: { fontSize: 14, fontWeight: 600, marginBottom: 2 },
      description: { fontSize: 13, lineHeight: 1.25, color: "#4B5563" },
    },
  });
};

const CandidatesActionTables = ({ onSearch = () => {}, selectedIds = [], jobId }) => {
  const navigate = useNavigate();
  const params = useParams();

  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const getInferredJobId = () => {
    return (
      jobId ||
      params.jobId ||
      params.jobid ||
      asObj(selectedIds?.[0])?.jobId ||
      asObj(selectedIds?.[0])?.jobid ||
      asObj(selectedIds?.[0])?.JobId ||
      null
    );
  };

  const handleShortlistClick = () => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      friendlyNotify(
        "Select a candidate",
        "Please pick at least one candidate to continue.",
        "yellow"
      );
      return;
    }

    const first = asObj(selectedIds[0]);
    const candidateId = first?.candidateId;
    const applicationId = first?.applicationId;

    if (!candidateId) {
      friendlyNotify(
        "Missing details",
        "We couldn't find the candidate ID. Please try again.",
        "red"
      );
      return;
    }

    navigate(`/candDashboard/${candidateId}`, {
      state: { candidateId, applicationId, selectedIds },
    });
  };

  const handleBulkCvClick = async () => {
    const inferredJobId = getInferredJobId();
    if (!inferredJobId) {
      friendlyNotify(
        "Choose a job",
        "Open a job page or select a row before downloading CVs.",
        "yellow"
      );
      return;
    }

    try {
      setIsBulkDownloading(true);
      friendlyNotify("Preparing CVs…", "We’re packaging all CVs for this job.");

      const token = localStorage.getItem("token") || "";
      const url = `${API_BASE_URL}/api/applications/job/${encodeURIComponent(
        inferredJobId
      )}/download-cvs`;

      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.status === 204) {
        friendlyNotify(
          "No CVs to download",
          "We didn’t find any CV files for this job.",
          "yellow"
        );
        return;
      }
      if (!res.ok) throw new Error("download failed");

      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      let filename = `job_${inferredJobId}_cvs.zip`;

      const m1 = /filename\*?=(?:UTF-8''|")(.*?)(?:"|$)/i.exec(cd);
      const m2 = m1 || /filename=([^;]+)/i.exec(cd);
      if (m2 && m2[1]) {
        try {
          filename = decodeURIComponent(m2[1].replace(/^"+|"+$/g, "").trim());
        } catch {
          filename = m2[1].replace(/^"+|"+$/g, "").trim();
        }
      }

      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);

      friendlyNotify("Download started", "Your CV ZIP is on the way.", "green");
    } catch (e) {
      friendlyNotify(
        "Download unavailable",
        "We couldn’t download the CVs right now. Please try again in a moment.",
        "red"
      );
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const handleExportExcelClick = async () => {
    const inferredJobId = getInferredJobId();
    if (!inferredJobId) {
      friendlyNotify(
        "Choose a job",
        "Open a job page or select a row before exporting.",
        "yellow"
      );
      return;
    }

    try {
      setIsExporting(true);
      friendlyNotify(
        "Preparing export…",
        "We’re generating the Excel sheet for this job."
      );

      const token = localStorage.getItem("token") || "";
      const url = `${API_BASE_URL}/api/applications/jobs/${encodeURIComponent(
        inferredJobId
      )}/export-excel`;

      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.status === 204) {
        friendlyNotify(
          "No data to export",
          "We didn’t find any applications for this job.",
          "yellow"
        );
        return;
      }
      if (!res.ok) throw new Error("export failed");

      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      let filename = `job_${inferredJobId}_applications.xlsx`;

      const m1 = /filename\*?=(?:UTF-8''|")(.*?)(?:"|$)/i.exec(cd);
      const m2 = m1 || /filename=([^;]+)/i.exec(cd);
      if (m2 && m2[1]) {
        try {
          filename = decodeURIComponent(m2[1].replace(/^"+|"+$/g, "").trim());
        } catch {
          filename = m2[1].replace(/^"+|"+$/g, "").trim();
        }
      }

      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);

      friendlyNotify("Export ready", "Your Excel file has been generated.", "green");
    } catch (e) {
      friendlyNotify(
        "Export unavailable",
        "We couldn’t export the Excel file right now. Please try again in a moment.",
        "red"
      );
    } finally {
      setIsExporting(false);
    }
  };

  const onSearchChange = (e) => {
    const val = e.target.value || "";
    setSearchValue(val);
    onSearch(val);
  };

  const searching = (searchValue || "").trim().length > 0;

  return (
    <div className="flex flex-wrap items-center w-full gap-y-2">
      {/* Search */}
      <div className="relative w-60">
        <FaMagnifyingGlass className="absolute justify-center text-gray-400 -translate-y-1/2 pointer-events-none left-4 top-1/2" />
        <input
          type="text"
          aria-label="Search candidates"
          placeholder="Search"
          value={searchValue}
          onChange={onSearchChange}
          className={[
            "w-full py-2 pl-10 pr-4 text-left rounded-full placeholder:text-left focus:outline-none transition-colors",
            searching ? "border-[#FFCC00]" : "border-gray-300",
            "border",
            "hover:border-[#FFCC00]", // hover -> yellow
            "focus:border-[#FFCC00]", // focus -> yellow
            "focus:ring-2 focus:ring-[#FFCC00]/40", // soft yellow glow
          ].join(" ")}
        />
      </div>

      <div className="flex items-center ml-auto gap-x-3">
        {/* Export Excel (LEFT of Bulk-CV) */}
        <Button
          className="bg-gray-100 shadow-md font-MTNfont"
          variant="default"
          radius="md"
          onClick={handleExportExcelClick}
          loading={isExporting}
        >
          <span className="flex items-center gap-2">
            <FaFileExcel size={18} />
            {isExporting ? "Exporting…" : "Export"}
          </span>
        </Button>

        {/* Bulk-CV */}
        <Button
          className="bg-gray-100 shadow-md font-MTNfont"
          variant="default"
          radius="md"
          onClick={handleBulkCvClick}
          loading={isBulkDownloading}
        >
          <span className="flex items-center gap-2">
            <FaSquareCaretDown size={18} />
            {isBulkDownloading ? "Preparing…" : "Bulk-CV"}
          </span>
        </Button>

        {/* View Candidate */}
        <Button
          radius="md"
          onClick={handleShortlistClick}
          className="text-black shadow-md font-MTNfont ps-6 bg-[#FFCC00] hover:bg-[#FFCC00]"
          leftIcon={
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/60">
              <FaCircleCheck size={14} className="text-black" />
            </span>
          }
        >
          View Candidate
        </Button>
      </div>
    </div>
  );
};

export default CandidatesActionTables;
